import React, { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, GitCompareArrows, Info } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

/**
 * Phase 5.4 — Legacy redirect.
 *
 * The original `Comparativo de Tipos` (POST /api/reports/type-comparison) was
 * extracted into its own `/comparatives` module. To keep already-shared
 * public links and bookmarks alive, this page now renders a friendly
 * redirect banner with a CTA → `/comparatives`. Query params (date range,
 * type_a, type_b, granularity, ...) are forwarded so the destination can
 * pre-fill the builder if it wants.
 *
 * NOTE: backend endpoint `/api/reports/type-comparison` is intentionally
 * kept functional for back-compat with public shares.
 */
export default function TypeComparison() {
  const navigate = useNavigate();
  const location = useLocation();

  const goToComparatives = () => {
    // Forward existing query params (and append a marker so /comparatives can
    // pre-open the builder pre-filled if it chooses to).
    const sp = new URLSearchParams(location.search);
    sp.set("from", "type-comparison");
    navigate(`/comparatives?${sp.toString()}`);
  };

  // Auto-redirect after 4 seconds so distracted users still land in the new
  // module without an extra click.
  useEffect(() => {
    const timer = setTimeout(goToComparatives, 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-4" data-testid="type-comparison-redirect">
      <Card>
        <CardContent className="space-y-3 pt-5">
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-primary/10 p-2 text-primary">
              <GitCompareArrows className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h2 className="text-lg font-semibold">
                Esta vista se movió a Comparativos
              </h2>
              <p className="text-sm text-muted-foreground">
                Ahora puedes comparar <strong>hasta 5 tipos</strong> de transacción
                y guardar tus configuraciones favoritas. Te redirigimos
                automáticamente en unos segundos.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 px-3 py-2 text-xs text-muted-foreground">
            <Info className="h-3 w-3 text-amber-500" />
            Si compartiste un link público de esta página, sigue funcionando — se redirige al módulo nuevo automáticamente.
          </div>
          <Button onClick={goToComparatives} data-testid="goto-comparatives-cta">
            Ir al módulo de Comparativos
            <ArrowRight className="ml-1.5 h-4 w-4" />
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
